import styled, { css, keyframes } from 'styled-components';
import { ifProp, palette, theme } from 'styled-tools';

import { FontWeights, minWidth, PaletteColor, Text, units } from '@calm-web/design-system';

import Header from '@/components/header/Header';
import { HeaderGridWrapper } from '@/components/header/Header/styles';
import GridWrapper from '@/components/ui/GridWrapper';

export const PreSignupFlowBackgroundWrapper = styled.div`
	height: 100%;
	background: ${palette('gray0')};
	overflow: scroll;
`;

export const ProgressBarContainer = styled.div`
	width: 100%;
	background: ${palette('gray0')};
`;

export const fadeIn = (startingPoint: number, percentComplete: number) => keyframes`
  from {
    width: ${startingPoint}%;
  } to {
    width: ${percentComplete}%;
  }
`;

export const ProgressBar = styled.div<{ percentComplete?: number; startingPoint: number }>`
	background: ${palette('accessibleBrandGradient')};
	width: ${p => p.percentComplete ?? 0}%;
	height: 6px;
	animation: ${props => fadeIn(props.startingPoint ?? 0, props.percentComplete ?? 0)} 0.75s ease-in;
`;

export const PreSignupFlowHeader = styled(Header)<{
	isForHandoff: boolean;
}>`
	background: white;
	height: ${theme('app.mobileHeaderHeight')};
	${p =>
		p.isForHandoff &&
		css`
			position: absolute;

			&:after {
				background: transparent;
			}

			svg {
				& path {
					fill: ${palette('black')};
				}
			}

			${minWidth('desktop')} {
				svg {
					& path {
						fill: ${palette('white')};
					}
				}
			}
		`}

	${minWidth('tablet')} {
		height: ${theme('app.headerHeight')};
	}

	${HeaderGridWrapper} {
		max-width: none;
	}
`;

export const PreSignupFlowGridWrapper = styled(GridWrapper)<{
	hideMargin?: boolean;
}>`
	margin: ${ifProp('hideMargin', '0', '0 16px')};

	${minWidth('desktop')} {
		margin: ${ifProp('hideMargin', '0', '0 16px')};
	}
`;

export const Column = styled.div<{
	hasPadding: boolean;
	$ignoreColumnWidth?: boolean;
}>`
	display: flex;
	flex-direction: column;
	grid-column-start: 1;
	grid-column-end: 5;
	min-height: calc(100vh - 100px);
	padding-top: ${ifProp('hasPadding', '44px', '0')};
	position: relative;
	text-align: left;

	${minWidth('desktop')} {
		${p =>
			!p.$ignoreColumnWidth &&
			css`
				grid-column-start: 2;
				grid-column-end: 4;
			`};
	}

	${minWidth('desktop')} {
		margin: 30px 0;
		padding-top: ${ifProp('hasPadding', '42px', '0')};
	}
`;

export const Title = styled(Text).attrs({
	weight: FontWeights.Regular,
	styleAs: 'h4',
	el: 'h1',
})`
	margin-top: 0;
	margin-bottom: ${units(3.5)};
`;

export const GridlessScreen = styled.div<{
	scrollable?: boolean;
	headerPresent?: boolean;
	isBannerVisible?: boolean;
}>`
	position: absolute;
	left: 0;
	right: 0;
	bottom: 0;
	overflow-y: ${ifProp('scrollable', 'scroll', 'hidden')};
	top: calc(
		${ifProp('headerPresent', theme('app.mobileHeaderHeight'), '0px')} +
			${ifProp('isBannerVisible', theme('app.mobileBannerHeight'), '0px')}
	);
	max-height: calc(
		100vh - ${ifProp('headerPresent', theme('app.mobileHeaderHeight'), '0')} -
			${ifProp('isBannerVisible', theme('app.mobileBannerHeight'), '0px')}
	);

	${minWidth('tablet')} {
		top: calc(
			${ifProp('headerPresent', theme('app.headerHeight'), '0px')} +
				${ifProp('isBannerVisible', theme('app.bannerHeight'), '0px')}
		);
		max-height: calc(
			100vh - ${ifProp('headerPresent', theme('app.headerHeight'), '0')} -
				${ifProp('isBannerVisible', theme('app.bannerHeight'), '0px')}
		);
	}
`;

export const Container = styled.div<{
	bgColor?: PaletteColor;
}>`
	${p =>
		p.bgColor &&
		css`
			background-color: ${palette(p.bgColor)};
		`}
`;
